import { z } from "zod";
import { AgentId, NodeId, Timestamp } from "./common.js";
import { ApprovalDecision, FsChangeKind, FsEntry, FsPath } from "./entities.js";
import { RunnerEvent, RunnerState, WakeBatch } from "./runner.js";
import { ErrorCodeSchema } from "./errors.js";

/**
 * 控制面 ↔ node-agent 协议（doc/api_v1.md §3）。
 * 传输：node-agent 主动外连 WS `/node`；帧为 JSON。
 * 三类帧：请求 req（控制面→节点）、响应 res（节点→控制面）、流事件 stream（节点→控制面）。
 */

export const ContainerSpec = z.object({
  agentId: AgentId,
  image: z.string(),
  cpus: z.number().positive(),
  memoryMb: z.number().int().positive(),
  pidsLimit: z.number().int().positive().default(512),
  /** 多租户节点使用 gVisor */
  runtime: z.enum(["runc", "runsc"]).default("runc"),
  /** 项目级 internal 网络名；唯一出口是节点 egress-proxy */
  network: z.string(),
  env: z.record(z.string(), z.string()).default({}),
  /** 该 agent 额外放行的出网域名（能力包 runtime.egress）：host、*.host，可带 :port */
  egressAllow: z.array(z.string().max(255)).max(256).default([]),
});
export type ContainerSpec = z.infer<typeof ContainerSpec>;

export const ConfigFile = z.object({
  /** 相对容器内 /home/agent */
  path: z.string(),
  content: z.string(),
  mode: z.number().int().default(0o644),
  /** skill 里的二进制文件（图片等）用 base64 */
  encoding: z.enum(["utf8", "base64"]).default("utf8"),
});

const Sha256 = z.string().regex(/^[0-9a-f]{64}$/);
/** 迁移包大小上限缺省值（8 GiB） */
export const DEFAULT_MIGRATION_MAX_BYTES = 8 * 1024 ** 3;

/** 迁移包信息（包格式是 node-agent 内部约定：agentsaas-migration.json + home/<i>.tar + work/**） */
export const MigrationResult = z.object({
  size: z.number().int(),
  sha256: Sha256,
  /** 包内条目数（清单 + home 存档 + work 下的文件 / 目录 / 符号链接） */
  entries: z.number().int(),
  /** 源节点保存的 ContainerSpec；控制面在目标节点 container.create 时原样使用 */
  spec: ContainerSpec.nullable(),
});
export type MigrationResult = z.infer<typeof MigrationResult>;

/** op → {args, result}；新增指令只在这里加一项。 */
export const NodeOps = {
  "container.create": { args: ContainerSpec, result: z.object({ containerId: z.string() }) },
  "container.configure": {
    args: z.object({
      agentId: AgentId,
      files: z.array(ConfigFile),
      /** 平台独占的目录（如 .claude/skills）：先清空再写，删掉的能力包不残留 */
      prune: z.array(z.string().min(1).max(256)).max(16).optional(),
      /** 整体替换该 agent 的额外出网白名单 */
      egressAllow: z.array(z.string().max(255)).max(256).optional(),
    }),
    result: z.object({}),
  },
  "container.start": { args: z.object({ agentId: AgentId }), result: z.object({}) },
  "container.stop": { args: z.object({ agentId: AgentId }), result: z.object({}) },
  "container.reset": { args: z.object({ agentId: AgentId }), result: z.object({}) },
  "container.destroy": { args: z.object({ agentId: AgentId, keepData: z.boolean().default(false) }), result: z.object({}) },
  /** 迁移（P5）：把 work 目录（+ runner 会话状态）打成 tar，PUT 到预签名 URL；容器在运行 → agent.busy */
  "container.export": {
    args: z.object({ agentId: AgentId, url: z.url(), maxBytes: z.number().int().positive().default(DEFAULT_MIGRATION_MAX_BYTES) }),
    result: MigrationResult,
  },
  /** 迁移（P5）：从预签名 URL 取回迁移包并解到本节点；容器已存在 → common.conflict；sha256 不符 → common.precondition_failed；包非法 → common.bad_request */
  "container.import": {
    args: z.object({ agentId: AgentId, url: z.url(), sha256: Sha256.optional(), maxBytes: z.number().int().positive().default(DEFAULT_MIGRATION_MAX_BYTES) }),
    result: MigrationResult,
  },
  "fs.tree": {
    args: z.object({ agentId: AgentId, path: FsPath, depth: z.number().int().min(1).max(5).default(1) }),
    result: z.object({ entries: z.array(FsEntry) }),
  },
  "fs.read": {
    args: z.object({ agentId: AgentId, path: FsPath, maxBytes: z.number().int().positive().default(2_000_000) }),
    result: z.object({ content: z.string(), encoding: z.enum(["utf8", "base64"]), etag: z.string(), size: z.number().int() }),
  },
  "fs.write": {
    args: z.object({
      agentId: AgentId,
      path: FsPath,
      content: z.string(),
      encoding: z.enum(["utf8", "base64"]).default("utf8"),
      ifMatch: z.string().optional(),
    }),
    result: z.object({ etag: z.string() }),
  },
  "fs.ops": {
    args: z.object({
      agentId: AgentId,
      op: z.enum(["move", "delete", "mkdir"]),
      path: FsPath,
      to: FsPath.optional(),
    }),
    result: z.object({}),
  },
  "fs.watch": { args: z.object({ agentId: AgentId, on: z.boolean() }), result: z.object({}) },
  /** 大文件直传（P4 资产）：node-agent 把 work 下的文件 PUT 到预签名 URL，内容不经过控制面 */
  "fs.upload": {
    args: z.object({ agentId: AgentId, path: FsPath, url: z.url(), contentType: z.string().optional(), maxBytes: z.number().int().positive().default(512_000_000) }),
    result: z.object({ size: z.number().int(), sha256: z.string() }),
  },
  /** 大文件直取（P4 资产发布）：从预签名 URL GET 到 work 下的 path（属主 1000） */
  "fs.download": {
    args: z.object({ agentId: AgentId, path: FsPath, url: z.url(), maxBytes: z.number().int().positive().default(512_000_000) }),
    result: z.object({ size: z.number().int(), sha256: z.string() }),
  },
  "pty.open": {
    args: z.object({ agentId: AgentId, cols: z.number().int().positive(), rows: z.number().int().positive() }),
    result: z.object({ ptyId: z.string() }),
  },
  /** data 为 UTF-8 文本（输出方向才用 base64） */
  "pty.input": { args: z.object({ ptyId: z.string(), data: z.string() }), result: z.object({}) },
  "pty.resize": {
    args: z.object({ ptyId: z.string(), cols: z.number().int().positive(), rows: z.number().int().positive() }),
    result: z.object({}),
  },
  "pty.close": { args: z.object({ ptyId: z.string() }), result: z.object({}) },
  "runner.deliver": { args: z.object({ agentId: AgentId, batch: WakeBatch }), result: z.object({ accepted: z.boolean() }) },
  "runner.control": {
    args: z.object({ agentId: AgentId, op: z.enum(["interrupt", "reload_config"]) }),
    result: z.object({}),
  },
  "runner.permission.reply": {
    args: z.object({ agentId: AgentId, requestId: z.string(), decision: ApprovalDecision, message: z.string().optional() }),
    result: z.object({}),
  },
} as const;

export type NodeOp = keyof typeof NodeOps;
export type NodeOpArgs<O extends NodeOp> = z.input<(typeof NodeOps)[O]["args"]>;
export type NodeOpResult<O extends NodeOp> = z.infer<(typeof NodeOps)[O]["result"]>;

export const NodeRequest = z.object({
  kind: z.literal("req"),
  id: z.string(),
  op: z.enum(Object.keys(NodeOps) as [NodeOp, ...NodeOp[]]),
  args: z.unknown(),
});
export type NodeRequest = z.infer<typeof NodeRequest>;

export const NodeResponse = z.union([
  z.object({ kind: z.literal("res"), id: z.string(), ok: z.literal(true), result: z.unknown() }),
  z.object({ kind: z.literal("res"), id: z.string(), ok: z.literal(false), code: ErrorCodeSchema, detail: z.string().optional() }),
]);
export type NodeResponse = z.infer<typeof NodeResponse>;

export const NodeLoad = z.object({
  cpu: z.number().min(0).max(1),
  memUsedMb: z.number().int().nonnegative(),
  memTotalMb: z.number().int().nonnegative(),
  diskFreeMb: z.number().int().nonnegative(),
});

export const ContainerState = z.enum(["creating", "running", "stopped", "missing", "error"]);

export const NodeStream = z.discriminatedUnion("stream", [
  z.object({
    kind: z.literal("stream"),
    stream: z.literal("heartbeat"),
    at: Timestamp,
    load: NodeLoad,
    containers: z.array(z.object({ agentId: AgentId, state: ContainerState })),
    /** 节点可用的容器运行时（runc / runsc …；P5，旧节点不发） */
    runtimes: z.array(z.string().max(32)).max(16).optional(),
  }),
  z.object({ kind: z.literal("stream"), stream: z.literal("container.status"), agentId: AgentId, state: ContainerState, reason: z.string().optional() }),
  z.object({ kind: z.literal("stream"), stream: z.literal("fs.changed"), agentId: AgentId, path: FsPath, change: FsChangeKind }),
  /** data 为 base64 编码的终端字节 */
  z.object({ kind: z.literal("stream"), stream: z.literal("pty.output"), ptyId: z.string(), data: z.string() }),
  z.object({ kind: z.literal("stream"), stream: z.literal("pty.exit"), ptyId: z.string(), code: z.number().int() }),
  z.object({ kind: z.literal("stream"), stream: z.literal("runner.event"), agentId: AgentId, event: RunnerEvent }),
  z.object({ kind: z.literal("stream"), stream: z.literal("runner.state"), agentId: AgentId, state: RunnerState }),
  z.object({ kind: z.literal("stream"), stream: z.literal("runner.permission.cancel"), agentId: AgentId, requestId: z.string(), reason: z.enum(["timeout", "interrupted", "aborted"]) }),
  z.object({
    kind: z.literal("stream"),
    stream: z.literal("runner.permission.request"),
    agentId: AgentId,
    requestId: z.string(),
    tool: z.string(),
    input: z.unknown(),
    reason: z.string().optional(),
  }),
]);
export type NodeStream = z.infer<typeof NodeStream>;

/** 握手：节点连上后第一帧 */
export const NodeHello = z.discriminatedUnion("auth", [
  z.object({ kind: z.literal("hello"), auth: z.literal("join"), joinToken: z.string(), hostInfo: z.object({ hostname: z.string(), os: z.string(), arch: z.string(), dockerVersion: z.string() }) }),
  z.object({ kind: z.literal("hello"), auth: z.literal("credential"), nodeId: NodeId, nodeCredential: z.string() }),
]);
export const NodeWelcome = z.object({ kind: z.literal("welcome"), nodeId: NodeId, nodeCredential: z.string().optional() });

export const FromNode = z.union([NodeHello, NodeResponse, NodeStream]);
export type FromNode = z.infer<typeof FromNode>;
export const ToNode = z.union([NodeWelcome, NodeRequest]);
export type ToNode = z.infer<typeof ToNode>;
